import { type CalendarDate, type Time, parseDate } from "@internationalized/date";
import { DateTime } from "luxon";
import type { WizardParkingChoice } from "../../PermitContextProvider/types";

const MINUTES_PER_DAY = 24 * 60;

/**
 * Latest selectable end date. A date works only if its earliest allowed end time (endTime.min,
 * else start of day) fits within duration.max, so the result can be a day before start + max.
 */
export const computeMaxEndDate = ({
	wizardSettings,
	startDate,
	startTime,
	timezone,
}: {
	wizardSettings: WizardParkingChoice["time"];
	startDate: CalendarDate | undefined;
	startTime: Time | undefined;
	timezone: string;
}): CalendarDate | undefined => {
	if (!wizardSettings.duration.max || !startDate || !startTime) {
		return wizardSettings.endDate.max ? parseDate(wizardSettings.endDate.max) : undefined;
	}

	if (wizardSettings.duration.max < MINUTES_PER_DAY && wizardSettings.endTime.max) {
		return startDate;
	}

	const startDateTime = DateTime.fromObject(
		{
			year: startDate.year,
			month: startDate.month,
			day: startDate.day,
			hour: startTime.hour,
			minute: startTime.minute,
		},
		{ zone: timezone },
	);
	const maxEndDateTime = startDateTime.plus({ minutes: wizardSettings.duration.max });

	const [hour, minute, second] = (wizardSettings.endTime.min ?? "00:00:00").split(":").map(Number);
	let lastEndDate = maxEndDateTime.set({
		hour: hour ?? 0,
		minute: minute ?? 0,
		second: second ?? 0,
		millisecond: 0,
	});
	if (lastEndDate > maxEndDateTime) {
		lastEndDate = lastEndDate.minus({ days: 1 });
	}
	return parseDate(lastEndDate.toFormat("yyyy-MM-dd"));
};
